import React, { useState, useEffect } from "react";
import { useAPI } from "../Context/MyContext";
import toast from "react-hot-toast";
import { Trash2, Upload, CheckCircle, Loader, X, Plus, User, Phone, FileText } from "lucide-react";

const ContactModal = ({ isOpen, onClose, onSuccess, contact }) => {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [attributes, setAttributes] = useState([{ key: "", value: "" }]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [activeTab, setActiveTab] = useState("manual"); // "manual" or "bulk"

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState({
    total: 0,
    inserted: 0,
    duplicates: 0,
    invalid: 0,
  });
  const [uploadType, setUploadType] = useState("standard");
  const [uploadDone, setUploadDone] = useState(false);

  const { createContact, contactFileCsv, updateContact, syncGoogleContacts } = useAPI();
  const isEdit = !!contact;

  // Reset form & upload state on open
  // useEffect(() => {
  //   if (isOpen) {
  //     if (isEdit && contact) {
  //       setName(contact.name || "");
  //       setPhone(contact.phone || "");

  //       setAttributes(
  //         Array.isArray(contact.attribute)
  //           ? contact.attribute.map((attr) => ({ key: attr.key, value: attr.value }))
  //           : [{ key: "", value: "" }]
  //       );
  //     } else {
  //       setName("");
  //       setPhone("");
  //       setAttributes([{ key: "", value: "" }]);

  //     }
  //     setSelectedFile(null);
  //     setUploadProgress(0);
  //     setUploadStats({ total: 0, inserted: 0, duplicates: 0, invalid: 0 });
  //     setUploadDone(false);
  //     setActiveTab("manual");
  //   }
  // }, [isOpen, isEdit, contact]);


  useEffect(() => {
    if (isOpen) {
      if (isEdit && contact) {
        setName(contact.name || "");
        setPhone(contact.phone || "");

        setAttributes(
          Array.isArray(contact.attribute)
            ? contact.attribute.map((attr) => ({
              key: attr.key,
              value: attr.value,
            }))
            : [{ key: "", value: "" }]
        );
      } else {
        setName("");
        setPhone("");
        setAttributes([{ key: "", value: "" }]);
      }

      // Reset upload states
      setUploadType("standard");
      setSelectedFile(null);
      setUploadProgress(0);

      setUploadStats({
        total: 0,
        inserted: 0,
        duplicates: 0,
        invalid: 0,
      });

      setUploadDone(false);
      setActiveTab("manual");
    }
  }, [isOpen, isEdit, contact]);



  // Manual contact submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phone) {
      toast.error("Phone number is required");
      return;
    }

    setIsSubmitting(true);
    const payload = {
      name: name || "Unnamed",
      phone: phone,
      attribute: attributes
        .filter((attr) => attr.key && attr.value)
        .map((attr) => ({ key: attr.key.trim(), value: attr.value.trim() })),
    };

    try {
      if (isEdit) {
        try {
          await updateContact(contact._id, payload);
          toast.success("Contact updated successfully!");
        }
        catch (err) {
          console.log("Error updating contact:", err);
        }

      } else {
        try {
          await createContact(payload);
          toast.success("Contact added successfully!");
        }
        catch (err) {
          console.log("Error creating contact:", err);
        }

      }
      onSuccess();
      setName("");
      setPhone("");
      setAttributes([{ key: "", value: "" }]);
      onClose();
    } catch (err) {
      // console.log(err);
      toast.error(err.message || "Failed to save contact.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAttributeChange = (index, field, value) => {
    const newAttributes = [...attributes];
    newAttributes[index][field] = value;
    setAttributes(newAttributes);
  };

  const addAttributeField = () => setAttributes([...attributes, { key: "", value: "" }]);
  const removeAttributeField = (index) =>
    setAttributes((prev) => prev.filter((_, i) => i !== index));

  // CSV file handlers
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv"))) {
      setSelectedFile(file);
      setUploadProgress(0);
      setUploadStats({ total: 0, inserted: 0, duplicates: 0, invalid: 0 });
      setUploadDone(false);
    } else {
      toast.error("Please upload a valid .csv file.");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv"))) {
      setSelectedFile(file);
      setUploadProgress(0);
      setUploadStats({ total: 0, inserted: 0, duplicates: 0, invalid: 0 });
      setUploadDone(false);
    } else {
      toast.error("Please upload a valid .csv file.");
    }
  };

  // // CSV upload
  // const handleFileUpload = async () => {
  //   if (!selectedFile) {
  //     toast.error("No file selected.");
  //     return;
  //   }

  //   setIsUploading(true);
  //   setUploadProgress(0);
  //   setUploadDone(false);

  //   const formData = new FormData();
  //   formData.append("file", selectedFile);

  //   const progressInterval = setInterval(() => {
  //     setUploadProgress((p) => (p < 90 ? p + 10 : p));
  //   }, 200);

  //   try {
  //     const response = await contactFileCsv(formData);

  //     clearInterval(progressInterval);
  //     setUploadProgress(100);

  //     const stats = response?.stats || {
  //       total: 0,
  //       inserted: 0,
  //       duplicates: 0,
  //       invalid: 0,
  //     };
  //     setUploadStats(stats);
  //     setUploadDone(true);
  //     onSuccess();
  //   } catch (err) {
  //     clearInterval(progressInterval);
  //     toast.error("CSV upload failed.");
  //     setUploadProgress(0);
  //   } finally {
  //     setIsUploading(false);
  //   }
  // };
  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error("No file selected.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadDone(false);

    const formData = new FormData();
    formData.append("file", selectedFile);

    const progressInterval = setInterval(() => {
      setUploadProgress((p) => (p < 90 ? p + 10 : p));
    }, 200);

    try {
      let response;

      if (uploadType === "google") {
        response = await syncGoogleContacts(formData);
      } else {
        response = await contactFileCsv(formData);
      }

      clearInterval(progressInterval);

      setUploadProgress(100);

      const stats = response?.stats || {
        total: 0,
        inserted: 0,
        duplicates: 0,
        invalid: 0,
      };

      setUploadStats(stats);
      setUploadDone(true);

      toast.success(
        uploadType === "google"
          ? "Google contacts imported successfully!"
          : "CSV uploaded successfully!"
      );

      onSuccess();
    } catch (err) {
      clearInterval(progressInterval);

      console.error(err);

      toast.error(
        uploadType === "google"
          ? "Google contacts upload failed."
          : "CSV upload failed."
      );

      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSampleCsv = () => {
    const csvContent = ["name,phone,country,city", "John Doe,14155552671,US,New York", "Ali Khan,923001234567,PK,Lahore"].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample_contacts.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting && !isUploading) {
          onClose();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-8 overflow-hidden animate-slideIn max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <User className="text-white" size={24} />
            </div>
            <h2 className="text-2xl font-bold text-white">
              {isEdit ? "Edit Contact" : "Add New Contact"}
            </h2>
          </div>
          <button
            onClick={() => !isSubmitting && !isUploading && onClose()}
            disabled={isSubmitting || isUploading}
            className="text-white/80 hover:text-white hover:bg-white/20 p-2 rounded-lg transition-all disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Tab Navigation - Only show for non-edit mode */}
          {!isEdit && (
            <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab("manual")}
                className={`flex-1 py-2.5 px-4 rounded-md font-medium transition-all flex items-center justify-center gap-2 ${activeTab === "manual"
                  ? "bg-white text-green-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
                  }`}
              >
                <User size={18} />
                Single Contact
              </button>
              <button
                onClick={() => setActiveTab("bulk")}
                className={`flex-1 py-2.5 px-4 rounded-md font-medium transition-all flex items-center justify-center gap-2 ${activeTab === "bulk"
                  ? "bg-white text-green-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
                  }`}
              >
                <FileText size={18} />
                Bulk Upload
              </button>
            </div>
          )}

          {/* Manual Contact Form */}
          {(isEdit || activeTab === "manual") && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name & Phone Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <User size={16} className="text-gray-400" />
                    Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter name"
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Phone size={16} className="text-gray-400" />
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter phone number"
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    disabled={isSubmitting}
                    required
                  />
                </div>
              </div>

              {/* Dynamic Attributes */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <FileText size={16} className="text-gray-400" />
                    Custom Attributes
                  </label>
                  <button
                    type="button"
                    onClick={addAttributeField}
                    className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1.5 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-all"
                    disabled={isSubmitting}
                  >
                    <Plus size={16} />
                    Add Attribute
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-64 overflow-y-auto custom-scrollbar">
                    {attributes.map((attr, i) => (
                      <div key={i} className="flex items-center border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors">
                        <div className="flex-1 flex items-center gap-3 p-3">
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Attribute key (e.g. country)"
                              value={attr.key}
                              onChange={(e) => handleAttributeChange(i, "key", e.target.value)}
                              className="w-full border-0 border-b border-gray-200 px-2 py-1.5 focus:outline-none focus:border-green-500 text-sm bg-transparent placeholder:text-gray-400"
                              disabled={isSubmitting}
                            />
                          </div>
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Attribute value (e.g. USA)"
                              value={attr.value}
                              onChange={(e) => handleAttributeChange(i, "value", e.target.value)}
                              className="w-full border-0 border-b border-gray-200 px-2 py-1.5 focus:outline-none focus:border-green-500 text-sm bg-transparent placeholder:text-gray-400"
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttributeField(i)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-3 transition-all flex-shrink-0 border-l border-gray-200"
                          disabled={isSubmitting}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                  {attributes.length === 0 && (
                    <div className="p-8 text-center text-gray-400 text-sm">
                      No custom attributes yet. Click "Add Attribute" to create one.
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg hover:from-green-700 hover:to-emerald-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    Processing...
                  </>
                ) : isEdit ? (
                  <>
                    <CheckCircle size={20} />
                    Update Contact
                  </>
                ) : (
                  <>
                    <Plus size={20} />
                    Add Contact
                  </>
                )}
              </button>
            </form>
          )}

          {/* CSV Upload Section */}
          {!isEdit && activeTab === "bulk" && (
            <div className="space-y-5">
              {/* Sample CSV Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 mb-3 font-medium">
                  📋 Need a template? Download our sample CSV file
                </p>
                <button
                  type="button"
                  onClick={downloadSampleCsv}
                  className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 text-sm font-medium bg-white px-4 py-2 rounded-lg hover:bg-blue-100 transition-all border border-blue-300"
                >
                  <Upload size={16} />
                  Download Sample CSV
                </button>
              </div>


              {/* Upload Type Selector */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between gap-2 flex-col sm:flex-row">

                  <div>
                    <h3 className="font-semibold text-gray-800">
                      Import Format
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                      Choose the contact file format before uploading
                    </p>
                  </div>

                  <div className="flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => setUploadType("standard")}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 sm:flex-none ${uploadType === "standard"
                        ? "bg-white shadow text-green-700"
                        : "text-gray-600 hover:text-gray-800"
                        }`}
                    >
                      Standard CSV
                    </button>

                    <button
                      type="button"
                      onClick={() => setUploadType("google")}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex-1 sm:flex-none ${uploadType === "google"
                        ? "bg-white shadow text-green-700"
                        : "text-gray-600 hover:text-gray-800"
                        }`}
                    >
                      Google CSV
                    </button>
                  </div>
                </div>

                {uploadType === "google" && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      Upload CSV files exported directly from Google Contacts.
                    </p>

                    <ol className="text-xs text-green-700 mt-2 space-y-1 list-decimal list-inside">
                      <li>Open Google Contacts</li>
                      <li>Select Export</li>
                      <li>Choose Google CSV format</li>
                      <li>Upload the exported file here</li>
                    </ol>
                  </div>
                )}
              </div>

              {/* Drag & Drop Area */}
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${dragOver
                  ? "bg-green-50 border-green-500 scale-[1.02]"
                  : "border-gray-300 hover:border-green-400 hover:bg-gray-50"
                  } ${isUploading ? "pointer-events-none opacity-50" : ""}`}
                onClick={() => !isUploading && document.getElementById("csvInput").click()}
              >
                <input
                  id="csvInput"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />

                {isUploading ? (
                  <div className="space-y-4">
                    <div className="inline-block p-4 bg-green-100 rounded-full">
                      <Loader className="animate-spin text-green-600" size={40} />
                    </div>
                    <div>
                      <p className="text-green-700 font-semibold text-lg mb-2">Uploading CSV...</p>
                      <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-2 font-medium">{uploadProgress}% completed</p>
                    </div>
                  </div>
                ) : selectedFile ? (
                  <div className="space-y-3">
                    <div className="inline-block p-4 bg-green-100 rounded-full">
                      <CheckCircle className="text-green-600" size={40} />
                    </div>
                    <div>
                      <p className="text-green-700 font-semibold text-lg">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500 mt-1">Ready to upload</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="inline-block p-4 bg-gray-100 rounded-full">
                      <Upload className="text-gray-400" size={40} />
                    </div>
                    <div>
                      <p className="text-gray-700 font-medium text-lg">Drop your CSV file here</p>
                      <p className="text-sm text-gray-500 mt-1">or click to browse</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              {selectedFile && !isUploading && !uploadDone && (
                <button
                  onClick={handleFileUpload}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg hover:from-green-700 hover:to-emerald-700 font-medium flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl"
                >
                  <Upload size={20} />
                  {/* Upload CSV File */}
                  {uploadType === "google"
                    ? "Import Google Contacts"
                    : "Upload CSV File"}
                </button>
              )}

              {/* Upload Stats */}
              {uploadDone && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 space-y-4">
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="text-green-600" size={24} />
                    <p className="text-lg font-bold text-green-800">Upload Complete!</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                      <p className="text-2xl font-bold text-gray-800">{uploadStats.total}</p>
                      <p className="text-sm text-gray-600 mt-1">Total Rows</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border border-green-200">
                      <p className="text-2xl font-bold text-green-700">{uploadStats.inserted}</p>
                      <p className="text-sm text-gray-600 mt-1">Inserted</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border border-yellow-200">
                      <p className="text-2xl font-bold text-yellow-700">{uploadStats.duplicates}</p>
                      <p className="text-sm text-gray-600 mt-1">Duplicates</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border border-red-200">
                      <p className="text-2xl font-bold text-red-700">{uploadStats.invalid}</p>
                      <p className="text-sm text-gray-600 mt-1">Invalid</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <button
            onClick={() => {
              if (!isSubmitting && !isUploading) {
                setSelectedFile(null);
                onClose();
              }
            }}
            disabled={isSubmitting || isUploading}
            className="w-full bg-white border border-gray-300 text-gray-700 py-2.5 rounded-lg hover:bg-gray-100 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting || isUploading ? "Processing..." : "Cancel"}
          </button>
        </div>
      </div>

    </div>
  );
};

export default ContactModal;